import { useEffect, useRef, useState } from "react";
import { MoreVertical, Pencil, History, Trash2, Star, X } from "lucide-react";
import { StatusBadge } from "@/components/admin/ui";
import { ServiceAreaMiniMap } from "./ServiceAreaMiniMap";

const ServiceAreaActionsMenu = ({ area, onToggleStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`org-service-area-menu-btn-${area.service_area_id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white">
        <MoreVertical size={17} />
      </button>
      {open && (
        <div data-testid={`org-service-area-menu-${area.service_area_id}`} className="absolute right-0 top-9 z-20 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl dark:border-white/10 dark:bg-[#111C33]">
          <button
            type="button"
            onClick={() => { setOpen(false); onToggleStatus(area); }}
            data-testid={`org-service-area-toggle-${area.service_area_id}`}
            className="flex w-full items-center px-3.5 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/10"
          >
            {area.status === "Active" ? "Deactivate" : "Activate"}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onDelete(area); }}
            data-testid={`org-service-area-delete-${area.service_area_id}`}
            className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-semibold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            <Trash2 size={14} /> Delete…
          </button>
        </div>
      )}
    </div>
  );
};

export const ServiceAreaCard = ({ area, isManager, availableTeams, onEdit, onToggleStatus, onAssignTeam, onUnassignTeam, onHistory, onDelete, savingId }) => {
  const coverage = area.description || area.geography_summary?.source_description || (area.geography_summary ? "Custom boundary" : "Boundary not set yet");

  return (
    <div className="rounded-2xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-service-area-card-${area.service_area_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-1 items-start gap-4">
          <ServiceAreaMiniMap preview={area.geography_preview} testId={`org-service-area-map-${area.service_area_id}`} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-bold text-slate-950 dark:text-white" data-testid={`org-service-area-name-${area.service_area_id}`}>{area.name}</p>
              <StatusBadge status={area.status} testId={`org-service-area-status-${area.service_area_id}`} />
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid={`org-service-area-coverage-${area.service_area_id}`}>{coverage}</p>

            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`org-service-area-teams-${area.service_area_id}`}>
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Teams:</span>
              {area.teams.length === 0 && <span className="text-xs text-slate-400">Unassigned</span>}
              {area.teams.map((t) => (
                <span key={t.team_id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  {t.leads?.length > 0 && <Star size={10} className="text-amber-500" fill="currentColor" />}
                  {t.name}
                  {isManager && (
                    <button type="button" onClick={() => onUnassignTeam(area.service_area_id, t.team_id)} data-testid={`org-service-area-remove-team-${area.service_area_id}-${t.team_id}`} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                  )}
                </span>
              ))}
              {isManager && (availableTeams || []).filter((t) => !area.teams.some((at) => at.team_id === t.team_id)).length > 0 && (
                <select
                  value=""
                  disabled={savingId === area.service_area_id}
                  onChange={(e) => onAssignTeam(area.service_area_id, e.target.value)}
                  data-testid={`org-service-area-add-team-${area.service_area_id}`}
                  className="rounded-lg border border-dashed border-slate-300 px-1.5 py-0.5 text-xs dark:border-white/15 dark:bg-white/5 dark:text-white"
                >
                  <option value="">+ Assign Team</option>
                  {availableTeams.filter((t) => !area.teams.some((at) => at.team_id === t.team_id)).map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                </select>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`org-service-area-services-${area.service_area_id}`}>
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Services:</span>
              {area.services.length === 0 ? (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">All Services</span>
              ) : (
                area.services.map((s) => <span key={s.service_id} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">{s.name}</span>)
              )}
            </div>
          </div>
        </div>

        {isManager && (
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => onHistory(area)} data-testid={`org-service-area-history-btn-${area.service_area_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
              <History size={13} /> History
            </button>
            <button type="button" onClick={() => onEdit(area)} data-testid={`org-service-area-edit-btn-${area.service_area_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
              <Pencil size={13} /> Edit
            </button>
            <ServiceAreaActionsMenu area={area} onToggleStatus={onToggleStatus} onDelete={onDelete} />
          </div>
        )}
      </div>
    </div>
  );
};
